import type { Rule } from 'antd/es/form';
import { requiredRule } from '../components/CustomerSidebar/customerFormSchema';

/**
 * TCR-2429 — ô "Nhân viên chăm sóc" (`care_staff_id`) trên POS mang HAI nghĩa khác nhau tuỳ
 * loại đơn:
 * - đơn B2C: nhân viên chăm sóc khách, danh sách = NV thuộc chi nhánh đang bán;
 * - đơn B2B: đây CHÍNH LÀ người được ghi nhận doanh số (BE: `care_staff_id || process_by`),
 *   danh sách = NVKD GT/MT toàn công ty (`GET /pos/staff?sales_channel=b2b`).
 *
 * Nên nhãn/placeholder/câu báo lỗi phải đổi theo chế độ: để nguyên "Nhân viên chăm sóc" thì
 * thu ngân không hiểu vì sao danh sách toàn người lạ (NVKD GT/MT không thuộc chi nhánh).
 * Gom về một chỗ để POS (`index.tsx`) và `CustomerCoreFields` không lệch chữ của nhau.
 */
export interface CareStaffFieldCopy {
  label: string;
  placeholder: string;
  /** Nhãn dùng cho rule required — B2B nói rõ "chọn" thay vì "nhập". */
  requiredMessage: string;
}

const CARE_STAFF_COPY_B2C: CareStaffFieldCopy = {
  label: 'Nhân viên chăm sóc',
  placeholder: 'Chọn nhân viên chăm sóc',
  requiredMessage: 'Vui lòng nhập nhân viên chăm sóc',
};

const CARE_STAFF_COPY_B2B: CareStaffFieldCopy = {
  label: 'NVKD ghi nhận doanh số',
  placeholder: 'Chọn NVKD ghi nhận doanh số',
  requiredMessage: 'Vui lòng chọn NVKD ghi nhận doanh số',
};

/** Nhãn/placeholder/câu lỗi của ô theo chế độ đơn. B2C giữ NGUYÊN VĂN chữ cũ. */
export function careStaffFieldCopy(isB2B: boolean): CareStaffFieldCopy {
  return isB2B ? CARE_STAFF_COPY_B2B : CARE_STAFF_COPY_B2C;
}

/**
 * Rule required của ô theo chế độ đơn.
 *
 * B2C vẫn gọi `requiredRule('nhân viên chăm sóc')` để câu báo lỗi y hệt trước TCR-2429
 * ("Vui lòng nhập nhân viên chăm sóc" — có test đang bám vào chuỗi này).
 */
export function careStaffRules(isB2B: boolean): Rule[] {
  if (!isB2B) return [requiredRule('nhân viên chăm sóc')];
  return [{ required: true, message: CARE_STAFF_COPY_B2B.requiredMessage }];
}

/**
 * TCR-2429 — giá trị mặc định của ô khi ĐỔI chế độ đơn (hoặc nạp lại tab).
 *
 * Vì sao phải đặt lại thay vì giữ giá trị đang chọn: hai chế độ lấy từ hai danh sách khác
 * nhau, FE không có cách nào kiểm tra tại chỗ một id còn hợp lệ với danh sách mới hay không
 * (chỉ BE biết). Giữ nguyên ⇒ rất dễ gửi lên một `care_staff_id` không thuộc danh sách và
 * BE ghi doanh số cho người không liên quan. Đặt lại về mặc định là giá trị CHẮC CHẮN hợp lệ.
 *
 * - B2C: chính mình (giữ nguyên hành vi trước ticket — tab mới luôn mặc định người đăng nhập).
 * - B2B: chỉ NVKD B2B mới được ghi nhận doanh số ⇒ NVKD thì chính mình; quản lý/kế toán/owner
 *   thì TRỐNG, buộc tự chọn NVKD (rule required chặn ghi đơn nếu bỏ qua).
 */
export function defaultCareStaffIdForMode(params: {
  isB2B: boolean;
  currentUserId?: string;
  /** `isB2BSalesUser(user)` — tài khoản đang đăng nhập có phải NVKD B2B. */
  currentUserIsB2BSales: boolean;
}): string | undefined {
  const { isB2B, currentUserId, currentUserIsB2BSales } = params;
  if (!currentUserId) return undefined;
  if (!isB2B) return currentUserId;
  return currentUserIsB2BSales ? currentUserId : undefined;
}
